// /signup-paciente?token=XXX — flow del paciente invitado por el kine.
//
// B8 — patrón AuthCallback:
// 1. Mount: lee `token` del query string. Valida vía RPC
//    validar_signup_token. Si OK, pre-fillea nombre/apellido/email.
//    Si error, redirect a /signup-paciente/error con code en state.
// 2. Form completar: email (editable post pre-fill), password
//    (+confirm), fecha de nacimiento, checkbox términos.
// 3. supabase.auth.signUp con options.data = { role, signup_token,
//    fecha_nacimiento }. Los datos viajan en raw_user_meta_data, NO se
//    llama RPC acá.
// 4. Redirect a /signup-paciente/email-enviado.
// 5. El paciente clickea el link del email → Supabase redirige a
//    /auth/callback → AuthCallback llama create_paciente_row con
//    sesión activa → redirige a /paciente-home.
//
// Token state errors al validar inicial van a /signup-paciente/error.
// Token state errors post-callback (consumido entre validate y submit)
// los maneja AuthCallback con redirect análogo.

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";

interface PrefilledData {
  nombre: string;
  apellido: string;
  email: string;
}

type Mode = "validating-token" | "idle" | "submitting";

export function SignupPaciente() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [prefill, setPrefill] = useState<PrefilledData | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<Mode>("validating-token");
  const [error, setError] = useState<string | null>(null);

  // Validar token al mount
  useEffect(() => {
    if (!token) {
      navigate("/signup-paciente/error", { state: { code: "TOKEN_INVALID" } });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: rpcError } = await supabase.rpc(
        "validar_signup_token",
        { p_token: token },
      );
      if (cancelled) return;
      if (rpcError) {
        navigate("/signup-paciente/error", {
          state: { code: "TOKEN_INVALID" },
        });
        return;
      }
      if (data && !data.ok) {
        navigate("/signup-paciente/error", { state: { code: data.code } });
        return;
      }
      // Pre-fill
      setPrefill({
        nombre: data.nombre ?? "",
        apellido: data.apellido ?? "",
        email: data.email ?? "",
      });
      setEmail(data.email ?? "");
      setMode("idle");
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Ingresá tu email");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña tiene que tener al menos 8 caracteres");
      return;
    }
    if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError("La contraseña tiene que incluir al menos 1 número o símbolo");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (!fechaNacimiento) {
      setError("Ingresá tu fecha de nacimiento");
      return;
    }
    // Client-side age check (RPC valida de nuevo)
    const birthDate = new Date(fechaNacimiento);
    const ageMs = Date.now() - birthDate.getTime();
    const ageYears = ageMs / (365.25 * 24 * 3600 * 1000);
    if (ageYears < 16) {
      setError(
        "Para crear cuenta tenés que tener al menos 16 años. Si sos menor, hablá con tu kinesiólogo.",
      );
      return;
    }
    if (!aceptaTerminos) {
      setError("Tenés que aceptar los términos y política de privacidad");
      return;
    }

    setMode("submitting");

    // signUp con metadata: token + fecha_nacimiento viajan en
    // raw_user_meta_data. AuthCallback los consume post email-confirm
    // para llamar create_paciente_row con sesión activa.
    const { data: signUpData, error: signUpError } =
      await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            role: "paciente",
            signup_token: token,
            fecha_nacimiento: fechaNacimiento,
          },
        },
      });

    if (signUpError) {
      const msg = signUpError.message.toLowerCase();
      if (
        msg.includes("already registered") ||
        msg.includes("user already") ||
        msg.includes("already exists")
      ) {
        setError(
          "Ya existe una cuenta con este email. Iniciá sesión o usá otro email.",
        );
      } else if (msg.includes("rate limit") || msg.includes("too many")) {
        setError("Intentaste muchas veces. Esperá unos minutos.");
      } else {
        setError(signUpError.message);
      }
      setMode("idle");
      return;
    }

    if (!signUpData.user) {
      setError("No pudimos crear tu cuenta. Probá de nuevo.");
      setMode("idle");
      return;
    }

    // Success — redirect a confirmación. AuthCallback completa el
    // linkeo de dominio post email-confirm.
    navigate("/signup-paciente/email-enviado", {
      state: { email: email.trim() },
    });
  };

  if (mode === "validating-token") {
    return (
      <AuthLayout title="Cargando…">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Activar tu cuenta de paciente"
      subtitle={
        prefill
          ? `Hola ${prefill.nombre}, completá tus datos para empezar.`
          : "Completá tus datos para empezar."
      }
      footer={
        <>
          ¿Ya tenés cuenta?{" "}
          <Link
            to="/login"
            className="text-primary hover:underline font-medium"
          >
            Iniciá sesión
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {prefill && (
          <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            <p>
              Nombre:{" "}
              <span className="text-foreground font-medium">
                {prefill.nombre} {prefill.apellido}
              </span>
            </p>
            <p className="text-xs mt-0.5">
              Lo cargó tu kinesiólogo. Si hay un error, contactalo.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1.5">
            Email
          </label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={mode === "submitting"}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Podés cambiarlo si preferís otro distinto al que tiene tu kine.
          </p>
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium mb-1.5"
          >
            Contraseña
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres + 1 número o símbolo"
              disabled={mode === "submitting"}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label={
                showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
              }
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirm"
            className="block text-sm font-medium mb-1.5"
          >
            Confirmar contraseña
          </label>
          <Input
            id="confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={mode === "submitting"}
          />
        </div>

        <div>
          <label
            htmlFor="fechaNacimiento"
            className="block text-sm font-medium mb-1.5"
          >
            Fecha de nacimiento
          </label>
          <Input
            id="fechaNacimiento"
            type="date"
            value={fechaNacimiento}
            onChange={(e) => setFechaNacimiento(e.target.value)}
            disabled={mode === "submitting"}
            max={new Date().toISOString().slice(0, 10)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Tenés que tener al menos 16 años para crear cuenta.
          </p>
        </div>

        <label className="flex items-start gap-2 cursor-pointer pt-2">
          <input
            type="checkbox"
            checked={aceptaTerminos}
            onChange={(e) => setAceptaTerminos(e.target.checked)}
            disabled={mode === "submitting"}
            className="mt-0.5 shrink-0"
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            Acepto los términos y política de privacidad de Gonix.
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-900 border border-red-200"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={mode === "submitting"}
        >
          {mode === "submitting" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creando cuenta…
            </>
          ) : (
            "Activar cuenta"
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
